/**
 * Layout component for BOSI - Body Signal Intelligence application.
 * Material Design themed with dark footer and elegant header.
 */

import React, { ReactNode } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

interface LayoutProps {
  children: ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { isAuthenticated, user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/auth');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const isActivePage = (path: string) => location.pathname === path;

  return (
    <div className="min-h-screen flex flex-col bg-primary-50">
      {/* Header */}
      <header className="bg-white shadow-soft sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-5">
            <div className="flex items-center space-x-12">
              <Link to="/" className="flex items-center">
                <h1 className="text-3xl font-serif font-bold text-primary-700 hover:text-primary-800 transition">
                  BOSI
                </h1>
              </Link>
              {isAuthenticated && (
                <nav className="hidden md:flex space-x-8">
                  <Link
                    to="/"
                    className={`text-sm font-medium transition-colors ${
                      isActivePage('/')
                        ? 'text-primary-700 border-b-2 border-primary-600 pb-1'
                        : 'text-neutral-600 hover:text-primary-700'
                    }`}
                  >
                    Home
                  </Link>
                  <Link
                    to="/progress"
                    className={`text-sm font-medium transition-colors ${
                      isActivePage('/progress')
                        ? 'text-primary-700 border-b-2 border-primary-600 pb-1'
                        : 'text-neutral-600 hover:text-primary-700'
                    }`}
                  >
                    Progress
                  </Link>
                  <Link
                    to="/bp-tracking"
                    className={`text-sm font-medium transition-colors ${
                      isActivePage('/bp-tracking')
                        ? 'text-primary-700 border-b-2 border-primary-600 pb-1'
                        : 'text-neutral-600 hover:text-primary-700'
                    }`}
                  >
                    BP Tracking
                  </Link>
                  <Link
                    to="/measurements"
                    className={`text-sm font-medium transition-colors ${
                      isActivePage('/measurements')
                        ? 'text-primary-700 border-b-2 border-primary-600 pb-1'
                        : 'text-neutral-600 hover:text-primary-700'
                    }`}
                  >
                    Measurements
                  </Link>
                  <Link
                    to="/profile"
                    className={`text-sm font-medium transition-colors ${
                      isActivePage('/profile')
                        ? 'text-primary-700 border-b-2 border-primary-600 pb-1'
                        : 'text-neutral-600 hover:text-primary-700'
                    }`}
                  >
                    Profile
                  </Link>
                </nav>
              )}
            </div>
            <div className="flex items-center space-x-4">
              {isAuthenticated ? (
                <>
                  <span className="hidden sm:block text-sm text-neutral-600 font-medium">
                    {user?.email}
                  </span>
                  <button
                    onClick={handleLogout}
                    className="btn btn-outline text-sm px-5 py-2"
                  >
                    Logout
                  </button>
                </>
              ) : (
                <Link to="/auth" className="btn btn-primary text-sm px-5 py-2">
                  Login
                </Link>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-grow max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {children}
      </main>

      {/* Dark Footer inspired by theme */}
      <footer className="bg-dark text-white mt-auto">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
            {/* About Section */}
            <div>
              <h3 className="text-xl font-serif font-semibold mb-4">About BOSI</h3>
              <p className="text-neutral-300 text-sm leading-relaxed">
                BOSI - Body Signal Intelligence supports women through perimenopause and menopause with personalized wellness guidance and evidence-based recommendations.
              </p>
            </div>

            {/* Quick Links */}
            <div>
              <h3 className="text-xl font-serif font-semibold mb-4">Quick Links</h3>
              <ul className="space-y-2">
                <li>
                  <Link to="/" className="text-neutral-300 hover:text-white text-sm transition">
                    Daily Check-In
                  </Link>
                </li>
                <li>
                  <Link to="/progress" className="text-neutral-300 hover:text-white text-sm transition">
                    View Progress
                  </Link>
                </li>
                <li>
                  <Link to="/bp-tracking" className="text-neutral-300 hover:text-white text-sm transition">
                    BP Tracking
                  </Link>
                </li>
                <li>
                  <Link to="/measurements" className="text-neutral-300 hover:text-white text-sm transition">
                    Body Measurements
                  </Link>
                </li>
                <li>
                  <Link to="/profile" className="text-neutral-300 hover:text-white text-sm transition">
                    Profile Settings
                  </Link>
                </li>
              </ul>
            </div>

            {/* Support */}
            <div>
              <h3 className="text-xl font-serif font-semibold mb-4">Support</h3>
              <ul className="space-y-2">
                <li>
                  <a href="#" className="text-neutral-300 hover:text-white text-sm transition">
                    Help Center
                  </a>
                </li>
                <li>
                  <a href="#" className="text-neutral-300 hover:text-white text-sm transition">
                    Contact Us
                  </a>
                </li>
                <li>
                  <a href="#" className="text-neutral-300 hover:text-white text-sm transition">
                    Privacy Policy
                  </a>
                </li>
              </ul>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="border-t border-neutral-700 pt-8 flex flex-col md:flex-row justify-between items-center">
            <p className="text-sm text-neutral-400 mb-4 md:mb-0">
              © 2026 BOSI - Body Signal Intelligence — <a href="#" className="hover:text-white transition">Terms</a> / <a href="#" className="hover:text-white transition">Privacy</a>
            </p>
            <div className="flex space-x-6">
              <a href="#" className="text-neutral-400 hover:text-white transition" aria-label="Facebook">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                </svg>
              </a>
              <a href="#" className="text-neutral-400 hover:text-white transition" aria-label="Twitter">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/>
                </svg>
              </a>
              <a href="#" className="text-neutral-400 hover:text-white transition" aria-label="Instagram">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                </svg>
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Layout;
